import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
/**
 * Slingu — um blob azul semi-3D vivo. Mantém um estado interno de "piscar"
 * que dispara em intervalos aleatórios (3–5 s), dando a sensação de vida
 * mesmo em repouso. Moods (`happy`, `sad`, `focus`, `wave`, `sleep`) trocam
 * a curva da boca, abertura dos olhos e, no caso do `wave`, agita as asas.
 */
export function SlinguAvatar({ size = 96, mood = 'idle', wings = true, still = false, className, }) {
    const [blinking, setBlinking] = useState(false);
    // Refs pra o loop de blink ler o mood/still CURRENTE sem re-montar o
    // timer. Antes o effect dependia de `[still, mood]` — cada troca de
    // mood reiniciava o timer e o blink raramente disparava. Agora roda
    // continuamente, só checa os refs na hora de piscar.
    const stillRef = useRef(still);
    stillRef.current = still;
    const moodRef = useRef(mood);
    moodRef.current = mood;
    useEffect(() => {
        let cancelled = false;
        let timeoutId = null;
        const loop = () => {
            // Intervalo natural: humanos piscam a cada ~3-6s. Se for happy/focus
            // encurta um pouco pra compensar a abertura reduzida dos olhos.
            const waitMs = 2400 + Math.random() * 3200;
            timeoutId = window.setTimeout(() => {
                if (cancelled)
                    return;
                if (stillRef.current || moodRef.current === 'sleep') {
                    // Mood atual não quer blinking — tenta de novo depois
                    loop();
                    return;
                }
                setBlinking(true);
                timeoutId = window.setTimeout(() => {
                    if (!cancelled)
                        setBlinking(false);
                    loop();
                }, 150);
            }, waitMs);
        };
        loop();
        return () => {
            cancelled = true;
            if (timeoutId !== null)
                clearTimeout(timeoutId);
        };
    }, []);
    const isClosed = blinking || mood === 'sleep';
    // Quando o mood é happy/focus os olhos já ficam semi-abertos (55-70%),
    // então o blink precisa ir a quase zero pra ser visível como piscada.
    const baseOpen = mood === 'happy' ? 0.6 : mood === 'focus' ? 0.75 : 1;
    const eyeScaleY = isClosed ? 0.04 : baseOpen;
    const mouth = mood === 'happy'
        ? 'M 28 40 Q 36 52, 44 40'
        : mood === 'sad'
            ? 'M 28 46 Q 36 38, 44 46'
            : mood === 'focus'
                ? 'M 30 44 L 42 44'
                : mood === 'sleep'
                    ? 'M 30 44 L 42 44'
                    : mood === 'wave'
                        ? 'M 28 40 Q 36 48, 44 40'
                        : 'M 30 42 Q 36 46, 42 42';
    const classNames = [
        'sl-avatar',
        !still ? 'sl-avatar--alive' : '',
        mood === 'wave' ? 'sl-avatar--wave' : '',
        mood === 'sleep' ? 'sl-avatar--sleep' : '',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 72 72", width: size, height: size, className: classNames, role: "img", "aria-label": "Slingu", children: [_jsxs("defs", { children: [_jsxs("radialGradient", { id: "sl-av-body", cx: "35%", cy: "30%", r: "75%", children: [_jsx("stop", { offset: "0%", stopColor: "rgb(137, 218, 255)" }), _jsx("stop", { offset: "55%", stopColor: "rgb(28, 176, 246)" }), _jsx("stop", { offset: "100%", stopColor: "rgb(20, 83, 163)" })] }), _jsxs("radialGradient", { id: "sl-av-highlight", cx: "30%", cy: "20%", r: "20%", children: [_jsx("stop", { offset: "0%", stopColor: "rgba(255,255,255,0.9)" }), _jsx("stop", { offset: "100%", stopColor: "rgba(255,255,255,0)" })] }), _jsxs("radialGradient", { id: "sl-av-shadow", cx: "50%", cy: "90%", r: "40%", children: [_jsx("stop", { offset: "0%", stopColor: "rgba(9, 47, 119, 0.35)" }), _jsx("stop", { offset: "100%", stopColor: "rgba(9, 47, 119, 0)" })] }), _jsxs("linearGradient", { id: "sl-av-wing", x1: "0", y1: "0", x2: "1", y2: "1", children: [_jsx("stop", { offset: "0%", stopColor: "rgba(255,255,255,0.85)" }), _jsx("stop", { offset: "100%", stopColor: "rgba(137, 218, 255, 0.5)" })] })] }), _jsx("ellipse", { className: "sl-avatar__shadow", cx: "36", cy: "66", rx: "20", ry: "3", fill: "rgba(9, 47, 119, 0.18)" }), wings ? (_jsxs("g", { className: "sl-avatar__wings", children: [_jsx("path", { d: "M 14 30 Q 2 22, 8 42 Q 16 42, 18 36 Z", fill: "url(#sl-av-wing)", stroke: "rgba(20, 83, 163, 0.35)", strokeWidth: "1" }), _jsx("path", { d: "M 58 30 Q 70 22, 64 42 Q 56 42, 54 36 Z", fill: "url(#sl-av-wing)", stroke: "rgba(20, 83, 163, 0.35)", strokeWidth: "1" })] })) : null, _jsxs("g", { className: "sl-avatar__body", children: [_jsx("path", { d: "M 14 28 C 14 14, 58 14, 58 28 C 62 42, 56 58, 36 58 C 16 58, 10 42, 14 28 Z", fill: "url(#sl-av-body)" }), _jsx("ellipse", { cx: "26", cy: "22", rx: "10", ry: "6", fill: "url(#sl-av-highlight)" }), _jsx("ellipse", { cx: "36", cy: "54", rx: "22", ry: "6", fill: "url(#sl-av-shadow)" }), _jsxs("g", { className: "sl-avatar__eyes", style: {
                            transform: `scaleY(${eyeScaleY})`,
                            transformOrigin: '36px 34px',
                            transition: 'transform 130ms ease',
                        }, children: [_jsx("ellipse", { cx: "28", cy: "34", rx: "4", ry: "5", fill: "white" }), _jsx("ellipse", { cx: "44", cy: "34", rx: "4", ry: "5", fill: "white" }), _jsx("circle", { cx: "29", cy: "35", r: "2.2", fill: "rgb(19, 31, 36)" }), _jsx("circle", { cx: "45", cy: "35", r: "2.2", fill: "rgb(19, 31, 36)" }), _jsx("circle", { cx: "28.3", cy: "33.6", r: "0.8", fill: "white" }), _jsx("circle", { cx: "44.3", cy: "33.6", r: "0.8", fill: "white" })] }), _jsx("path", { d: mouth, stroke: "rgb(20, 83, 163)", strokeWidth: "2.5", strokeLinecap: "round", fill: "none" }), _jsx("circle", { cx: "22", cy: "44", r: "3", fill: "rgba(255, 134, 208, 0.55)" }), _jsx("circle", { cx: "50", cy: "44", r: "3", fill: "rgba(255, 134, 208, 0.55)" }), mood === 'sleep' ? (_jsxs("g", { className: "sl-avatar__zzz", fill: "rgb(63, 133, 167)", fontSize: "10", fontWeight: "700", children: [_jsx("text", { x: "56", y: "20", children: "z" }), _jsx("text", { x: "62", y: "14", children: "Z" })] })) : null] })] }));
}
//# sourceMappingURL=SlinguAvatar.js.map